import { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { useWorkspace } from '../context/workspace-context';
import { parseDocuments } from '../helpers/parse-documents';
import { Sentry } from '../lib/sentry';
import {
  COURSE_CONTENT_SOURCE,
  CourseContentSource,
} from '../constants/course-content-source';
import {
  QUESTION_TYPES,
  ALL_QUESTION_TYPES,
  QuestionType,
} from '../constants/question-types';
import { useCreateCourse } from '../hooks/use-course';
import { useDepartments } from '../hooks/use-department';
import {
  MAX_FILE_SIZE,
  ALLOWED_FILE_TYPES,
} from '../constants/document-upload';

interface ValidationErrors {
  courseName?: string;
  courseDescription?: string;
  departments?: string;
  file?: string;
  purpose?: string;
  subjectMatter?: string;
  numLevels?: string;
  questionsPerLevel?: string;
  questionTypes?: string;
}

export default function CreateCourse() {
  const navigate = useNavigate();
  const { currentWorkspace, isLoadingWorkspaces } = useWorkspace();
  const createCourse = useCreateCourse();
  const [courseName, setCourseName] = useState('');
  const [courseDescription, setCourseDescription] = useState('');
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<number[]>(
    [],
  );
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );
  const [purpose, setPurpose] = useState('');
  const [subjectMatter, setSubjectMatter] = useState('');
  const [numLevels, setNumLevels] = useState(5);
  const [questionsPerLevel, setQuestionsPerLevel] = useState(10);
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState<
    QuestionType[]
  >([...ALL_QUESTION_TYPES]);

  const isWorkspaceReady = !!currentWorkspace && !isLoadingWorkspaces;

  const { data: deptData } = useDepartments(
    currentWorkspace?.id ?? 0,
    isWorkspaceReady,
  );
  const departments = deptData?.departments ?? [];
  const departmentOptions = departments.map((dept) => ({
    value: dept.id,
    label: dept.name,
  }));

  const contentSource: CourseContentSource =
    uploadedFiles.length > 0
      ? COURSE_CONTENT_SOURCE.DOCUMENT
      : COURSE_CONTENT_SOURCE.AI;

  const handleDepartmentChange = (
    newValue: readonly { value: number; label: string }[] | null,
  ) => {
    if (newValue) {
      setSelectedDepartmentIds(newValue.map((option) => option.value));
      setValidationErrors((prev) => ({ ...prev, departments: undefined }));
    } else {
      setSelectedDepartmentIds([]);
    }
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    if (!courseName.trim()) {
      errors.courseName = 'Course name is required';
    } else if (courseName.length < 3) {
      errors.courseName = 'Course name must be at least 3 characters';
    } else if (courseName.length > 100) {
      errors.courseName = 'Course name must not exceed 100 characters';
    }

    if (!courseDescription.trim()) {
      errors.courseDescription = 'Course description is required';
    } else if (courseDescription.length < 4) {
      errors.courseDescription = 'Description must be at least 4 characters';
    } else if (courseDescription.length > 1000) {
      errors.courseDescription = 'Description must not exceed 1000 characters';
    }

    if (selectedDepartmentIds.length === 0) {
      errors.departments = 'At least one department/group must be selected';
    }

    if (!purpose.trim()) {
      errors.purpose = 'Course purpose is required';
    }

    if (!subjectMatter.trim()) {
      errors.subjectMatter = 'Subject matter is required';
    }

    if (numLevels < 1) {
      errors.numLevels = 'Number of levels must be at least 1';
    } else if (numLevels > 50) {
      errors.numLevels = 'Number of levels must not exceed 50';
    }

    if (questionsPerLevel < 1) {
      errors.questionsPerLevel = 'Questions per level must be at least 1';
    } else if (questionsPerLevel > 50) {
      errors.questionsPerLevel = 'Questions per level must not exceed 50';
    }

    if (selectedQuestionTypes.length === 0) {
      errors.questionTypes = 'At least one question type must be selected';
    }

    if (contentSource === COURSE_CONTENT_SOURCE.DOCUMENT) {
      const invalidFile = uploadedFiles.find(
        (file) => !ALLOWED_FILE_TYPES.includes(file.type),
      );
      if (invalidFile) {
        errors.file =
          'Invalid file type. Please upload .txt, .doc, .docx, or .pdf files';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (!currentWorkspace || isLoadingWorkspaces) {
        throw new Error('Workspace not loaded');
      }

      let parsedContent: { content: string; fileName: string }[] | undefined;

      // Parse documents if uploaded
      if (
        contentSource === COURSE_CONTENT_SOURCE.DOCUMENT &&
        uploadedFiles.length > 0
      ) {
        parsedContent = await parseDocuments(uploadedFiles);
      }

      const result = await createCourse.mutateAsync({
        name: courseName,
        description: courseDescription,
        departmentIds: selectedDepartmentIds,
        organisationId: currentWorkspace.id,
        purpose: purpose,
        subjectMatter: subjectMatter,
        creationMethod: contentSource,
        content: parsedContent,
      });

      navigate(`/analysis/${result.course.id}`, {
        state: {
          questionTypes: selectedQuestionTypes,
          numLevels,
          questionsPerLevel,
        },
      });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const MAX_FILES = 5;

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || []);
    if (newFiles.length === 0) return;

    const totalFiles = uploadedFiles.length + newFiles.length;
    if (totalFiles > MAX_FILES) {
      setValidationErrors((prev) => ({
        ...prev,
        file: `Maximum ${MAX_FILES} files allowed. You have ${uploadedFiles.length} file(s) already.`,
      }));
      return;
    }

    const oversizedFile = newFiles.find((file) => file.size > MAX_FILE_SIZE);
    if (oversizedFile) {
      setValidationErrors((prev) => ({
        ...prev,
        file: `"${oversizedFile.name}" exceeds the 10MB file size limit`,
      }));
      return;
    }

    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setValidationErrors((prev) => ({ ...prev, file: undefined }));

    // Reset the input so the same file(s) can be selected again if removed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex gap-8">
      {/* Left section - Form */}
      <div className="flex-1 bg-white p-8 rounded-3xl">
        <h1 className="text-2xl font-semibold mb-6">Create New Course</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="courseName" className="flex items-center gap-1">
              Course Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="courseName"
              placeholder="Enter course name"
              value={courseName}
              onChange={(e) => {
                setCourseName(e.target.value);
                setValidationErrors((prev) => ({
                  ...prev,
                  courseName: undefined,
                }));
              }}
              className={validationErrors.courseName ? 'border-red-500' : ''}
            />
            {validationErrors.courseName && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.courseName}
              </p>
            )}
          </div>

          <div>
            <Label
              htmlFor="courseDescription"
              className="flex items-center gap-1"
            >
              Course Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="courseDescription"
              placeholder="Enter course description"
              value={courseDescription}
              onChange={(e) => {
                setCourseDescription(e.target.value);
                setValidationErrors((prev) => ({
                  ...prev,
                  courseDescription: undefined,
                }));
              }}
              className={
                validationErrors.courseDescription ? 'border-red-500' : ''
              }
            />
            {validationErrors.courseDescription && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.courseDescription}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="departments" className="flex items-center gap-1">
              Team / Department <span className="text-red-500">*</span>
            </Label>
            <Select
              isMulti
              options={departmentOptions}
              onChange={handleDepartmentChange}
              placeholder="Select team / department..."
              className={validationErrors.departments ? 'border-red-500' : ''}
            />
            {validationErrors.departments && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.departments}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="purpose" className="flex items-center gap-1">
              Course Purpose <span className="text-red-500">*</span>
            </Label>
            <Input
              id="purpose"
              placeholder="e.g., employee onboarding, compliance training"
              value={purpose}
              onChange={(e) => {
                setPurpose(e.target.value);
                setValidationErrors((prev) => ({
                  ...prev,
                  purpose: undefined,
                }));
              }}
              className={validationErrors.purpose ? 'border-red-500' : ''}
            />
            <p className="text-sm text-gray-500 mt-1">
              What is the main goal of this course? Feel free to be as detailed
              as you want.
            </p>
            {validationErrors.purpose && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.purpose}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="subjectMatter" className="flex items-center gap-1">
              Subject Matter <span className="text-red-500">*</span>
            </Label>
            <Input
              id="subjectMatter"
              placeholder="e.g., cybersecurity, sales training, product knowledge"
              value={subjectMatter}
              onChange={(e) => {
                setSubjectMatter(e.target.value);
                setValidationErrors((prev) => ({
                  ...prev,
                  subjectMatter: undefined,
                }));
              }}
              className={validationErrors.subjectMatter ? 'border-red-500' : ''}
            />
            <p className="text-sm text-gray-500 mt-1">
              What topic or field does this course cover? Add as many details as
              you want.
            </p>
            {validationErrors.subjectMatter && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.subjectMatter}
              </p>
            )}
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="numLevels" className="flex items-center gap-1">
                Number of Levels <span className="text-red-500">*</span>
              </Label>
              <Input
                id="numLevels"
                type="number"
                value={numLevels}
                onChange={(e) => {
                  setNumLevels(parseInt(e.target.value) || 0);
                  setValidationErrors((prev) => ({
                    ...prev,
                    numLevels: undefined,
                  }));
                }}
                className={validationErrors.numLevels ? 'border-red-500' : ''}
              />
              {validationErrors.numLevels && (
                <p className="text-red-500 text-sm mt-1">
                  {validationErrors.numLevels}
                </p>
              )}
            </div>

            <div className="flex-1">
              <Label
                htmlFor="questionsPerLevel"
                className="flex items-center gap-1"
              >
                Questions per Level <span className="text-red-500">*</span>
              </Label>
              <Input
                id="questionsPerLevel"
                type="number"
                value={questionsPerLevel}
                onChange={(e) => {
                  setQuestionsPerLevel(parseInt(e.target.value) || 0);
                  setValidationErrors((prev) => ({
                    ...prev,
                    questionsPerLevel: undefined,
                  }));
                }}
                className={
                  validationErrors.questionsPerLevel ? 'border-red-500' : ''
                }
              />
              {validationErrors.questionsPerLevel && (
                <p className="text-red-500 text-sm mt-1">
                  {validationErrors.questionsPerLevel}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-1">
              Question Types <span className="text-red-500">*</span>
            </Label>
            <p className="text-sm text-gray-500 mb-2">
              Select which question types the AI should use for this course.
            </p>
            <div className="flex flex-col gap-3">
              {[
                {
                  value: QUESTION_TYPES.SINGLE_SELECT,
                  label: 'Multiple Choice (one answer)',
                },
                {
                  value: QUESTION_TYPES.MULTIPLE_SELECT,
                  label: 'Multiple Choice (many answers)',
                },
                {
                  value: QUESTION_TYPES.FILL_IN_BLANK,
                  label: 'Fill in the Blank',
                },
              ].map((type) => (
                <div key={type.value} className="flex items-center gap-2">
                  <Checkbox
                    id={type.value}
                    checked={selectedQuestionTypes.includes(type.value)}
                    onCheckedChange={(checked) => {
                      setSelectedQuestionTypes((prev) =>
                        checked
                          ? [...prev, type.value]
                          : prev.filter((t) => t !== type.value),
                      );
                      setValidationErrors((prev) => ({
                        ...prev,
                        questionTypes: undefined,
                      }));
                    }}
                  />
                  <Label htmlFor={type.value} className="cursor-pointer">
                    {type.label}
                  </Label>
                </div>
              ))}
            </div>
            {validationErrors.questionTypes && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.questionTypes}
              </p>
            )}
          </div>

          <div>
            <Input
              id="fileUpload"
              type="file"
              onChange={handleFileUpload}
              className="hidden"
              ref={fileInputRef}
              accept=".txt,.doc,.docx,.pdf"
              multiple
            />
            <div className="flex items-center gap-3 mb-4">
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadedFiles.length >= MAX_FILES}
                className="bg-[#14181F] text-white hover:bg-[#14181F]/90 rounded-full px-6 py-3 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload Document{uploadedFiles.length > 0 ? 's' : ''}
              </Button>
              {uploadedFiles.length > 0 && (
                <span className="text-sm text-gray-500">
                  {uploadedFiles.length} of {MAX_FILES} documents
                </span>
              )}
            </div>

            {uploadedFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="bg-white rounded-full py-3 px-6 flex items-center justify-between border mb-2"
              >
                <div className="flex items-center gap-2">
                  <div className="text-green-500">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-gray-700">{file.name}</span>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setUploadedFiles((prev) =>
                      prev.filter((_, i) => i !== index),
                    )
                  }
                  className="text-gray-500 hover:text-gray-700"
                >
                  <img
                    src="/images/delete.svg"
                    alt="Delete"
                    width="15"
                    height="15"
                  />
                </button>
              </div>
            ))}
            {validationErrors.file && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.file}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full bg-[#A78BFA] hover:bg-[#A78BFA]/90 text-white rounded-full py-3 flex items-center justify-center gap-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              'Creating Course...'
            ) : (
              <>
                Create Course
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 12H19M19 12L12 5M19 12L12 19"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Right section - Preview */}
      <div className="flex-1 bg-white p-8 rounded-3xl">
        {!courseName &&
        !courseDescription &&
        selectedDepartmentIds.length === 0 &&
        !purpose &&
        !subjectMatter ? (
          <div className="flex flex-col items-center pt-12">
            {/* Initial chat message layout */}
            <div className="flex items-end gap-2">
              <img
                src="/images/llaami-orange.svg"
                alt="Llaami Mascot"
                className="w-10 h-10 mb-1"
              />
              <div className="relative bg-[#FDF8F3] p-4 rounded-2xl">
                <div className="absolute bottom-[12px] -left-2 w-4 h-4 bg-[#FDF8F3] transform rotate-45"></div>
                <div className="relative z-10">
                  <p className="text-gray-700 text-[15px] leading-relaxed">
                    As you fill in the course details on the left, you will see
                    a preview of your course here.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6 text-lg pt-12">
            <p>
              I would like to create a{' '}
              {courseName ? (
                <span className="font-bold italic">{courseName}</span>
              ) : (
                <span className="font-bold italic text-gray-500">
                  course_name
                </span>
              )}{' '}
              about{' '}
              {courseDescription ? (
                <span className="font-bold italic">{courseDescription}</span>
              ) : (
                <span className="font-bold italic text-gray-500">
                  course_description
                </span>
              )}{' '}
              for{' '}
              {currentWorkspace?.name ? (
                <span className="font-bold italic">
                  {currentWorkspace.name}
                </span>
              ) : (
                <span className="font-bold italic text-gray-500">
                  organization_name
                </span>
              )}
              .
            </p>

            <p>
              For{' '}
              {selectedDepartmentIds.length > 0 ? (
                <span className="font-bold italic">
                  {departments
                    .filter((d) => selectedDepartmentIds.includes(d.id))
                    .map((d) => d.name)
                    .join(', ')}
                </span>
              ) : (
                <span className="font-bold italic text-gray-500">
                  team_department
                </span>
              )}{' '}
              who need to understand.
            </p>

            <p>
              The course will cover{' '}
              {purpose ? (
                <span className="font-bold italic">{purpose}</span>
              ) : (
                <span className="font-bold italic text-gray-500">
                  course_purpose
                </span>
              )}{' '}
              with the intention of{' '}
              {subjectMatter ? (
                <span className="font-bold italic">{subjectMatter}</span>
              ) : (
                <span className="font-bold italic text-gray-500">
                  subject_matter
                </span>
              )}
              .
            </p>

            <p>
              It intends to do so in{' '}
              {numLevels ? (
                <span className="font-bold italic">{numLevels}</span>
              ) : (
                <span className="font-bold italic text-gray-500">
                  number_of_levels
                </span>
              )}{' '}
              levels, consisting of{' '}
              {questionsPerLevel ? (
                <span className="font-bold italic">{questionsPerLevel}</span>
              ) : (
                <span className="font-bold italic text-gray-500">
                  questions_per_level
                </span>
              )}{' '}
              questions each.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
